import React, { useState } from 'react';
import { Badge, Button, Input } from 'reactstrap';
import { reviewSettlementPrice, correctSettlement } from '../../../services/settlementService';

// Review and correction for one price window.
//
// Before its batches settle, a window that moved more than 40% is held: the
// administrator confirms the move is real, corrects the start or end price
// (a split, a spin-off the window straddles), or excludes the stock from
// scoring in every batch that picked it. Once a batch has settled, the same
// fixes are made as a correction, which re-scores that batch.
const pct = v => (v == null ? '—' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(2)}%`);
const ACTION_LABEL = { confirm: 'Confirmed', adjust: 'Prices adjusted', exclude: 'Excluded from scoring' };
// Modes whose settled batches can be corrected here; member research is one asset per forecast.
const CORRECTABLE = ['stock-selection', 'event-gap', 'macro-rotation', 'extreme-movers', 'analyst-revision', 'extreme-movers-ohlc'];

export function MoveCell({ row }) {
  if (row.move == null) return null;
  const big = Math.abs(row.move) > 0.4;
  return <div className={big ? 'text-danger fw-semibold' : 'text-muted'} title="Change from start to end price">{pct(row.move)}</div>;
}

export function ReviewStatus({ row }) {
  if (row.needs_review) return <Badge color="danger" className="d-block mb-1">Needs review: moved {pct(row.move)}</Badge>;
  if (!row.review) return null;
  return (
    <div className="small mb-1">
      <Badge color={row.review.action === 'exclude' ? 'warning' : 'info'}>{ACTION_LABEL[row.review.action] || row.review.action}</Badge>{' '}
      <span className="text-muted">{row.review.note}</span>
      {row.review.original_start_price != null && <div className="text-muted">Fetched: {Number(row.review.original_start_price)} → {Number(row.review.original_end_price)}</div>}
    </div>
  );
}

export default function ReviewPanel({ row, onDone }) {
  const settled = row.predictions.length > 0 && row.predictions.every(p => p.status === 'settled');
  const groups = [...new Map(row.predictions.filter(p => p.status === 'settled' && CORRECTABLE.includes(p.mode))
    .map(p => [`${p.mode}|${p.group_id}${p.mode === 'analyst-revision' ? '|' + p.prediction_id : ''}`, { mode: p.mode, group_id: p.group_id, prediction_id: p.mode === 'analyst-revision' ? p.prediction_id : '', model: p.prediction?.model }])).values()];
  const [action, setAction] = useState(settled ? 'exclude' : 'confirm');
  const [group, setGroup] = useState(groups[0] ? `${groups[0].mode}|${groups[0].group_id}${groups[0].prediction_id ? '|' + groups[0].prediction_id : ''}` : '');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  if (row.status !== 'ready' && !settled) return null;
  if (settled && !groups.length) return null;

  async function submit(chosen = action) {
    setBusy(true);
    setMessage(null);
    try {
      if (settled) {
        const [mode, group_id, prediction_id] = group.split('|');
        const out = await correctSettlement({ mode, group_id, prediction_id, symbol: row.symbol, action: chosen, start_close: start, end_close: end, reason: note });
        setMessage({ ok: true, text: `Re-scored ${out.results?.length ?? 0} forecast(s); a published article is queued for update.` });
      } else {
        await reviewSettlementPrice(row.id, { action: chosen, start_price: start, end_price: end, note });
        setMessage({ ok: true, text: chosen === 'clear' ? 'Review withdrawn.' : 'Saved.' });
      }
      onDone && onDone();
    } catch (e) {
      setMessage({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  }

  const options = settled
    ? [['exclude', 'Exclude from scoring'], ['include', 'Score it again'], ['adjust', 'Adjust prices']]
    : [['confirm', 'Confirm the move is real'], ['adjust', 'Adjust prices'], ['exclude', 'Exclude from scoring']];

  return (
    <details className="mt-1" open={row.needs_review}>
      <summary className="small">{settled ? 'Correct settled forecast' : row.review ? 'Change review' : 'Review'}</summary>
      <div className="d-flex flex-column gap-1 mt-1" style={{ minWidth: 260 }}>
        {settled && groups.length > 1 && (
          <Input type="select" bsSize="sm" aria-label="Batch" value={group} onChange={e => setGroup(e.target.value)}>
            {groups.map(g => <option key={`${g.mode}|${g.group_id}|${g.prediction_id}`} value={`${g.mode}|${g.group_id}${g.prediction_id ? '|' + g.prediction_id : ''}`}>{g.mode} · {g.group_id.slice(0, 8)}{g.model ? ` · ${g.model}` : ''}</option>)}
          </Input>
        )}
        <Input type="select" bsSize="sm" aria-label="Action" value={action} onChange={e => setAction(e.target.value)}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Input>
        {action === 'adjust' && (
          <div className="d-flex gap-1">
            <Input bsSize="sm" type="number" step="any" placeholder={`start ${Number(row.start_price) || ''}`} value={start} onChange={e => setStart(e.target.value)} aria-label="Start price" />
            <Input bsSize="sm" type="number" step="any" placeholder={`end ${Number(row.end_price) || ''}`} value={end} onChange={e => setEnd(e.target.value)} aria-label="End price" />
          </div>
        )}
        <Input bsSize="sm" type="textarea" rows="2" placeholder="What was checked, and why (required)" value={note} onChange={e => setNote(e.target.value)} aria-label="Reason" />
        <div className="d-flex gap-1">
          <Button size="sm" color={action === 'exclude' ? 'warning' : 'primary'} disabled={busy || note.trim().length < 3} onClick={() => submit()}>{settled ? 'Re-score batch' : 'Save review'}</Button>
          {!settled && row.review && <Button size="sm" color="light" disabled={busy} onClick={() => submit('clear')}>Withdraw review</Button>}
        </div>
        {message && <div className={`small ${message.ok ? 'text-success' : 'text-danger'}`}>{message.text}</div>}
      </div>
    </details>
  );
}
